import type { DeepNonNullable } from 'ts-essentials';
import { proxy } from 'valtio';

import type { RequireAtLeastOne } from '@/utils/tsutils';

import type { UserFragment } from '@/graphql/saleor/api';

export interface UserState<Require = boolean> {
  isAuthenticated: null | boolean;
  isAuthenticating: boolean;
  isInitializing: boolean;
  isProcessing: boolean;
  user: Require extends false
    ? Partial<UserFragment> | null
    : DeepNonNullable<UserFragment>;
}

export const initialUserState: UserState = {
  user: null,
  isProcessing: false,
  isInitializing: false,
  isAuthenticated: null,
  isAuthenticating: false,
};

export const userState = proxy<UserState>({ ...initialUserState });

export const userActions = {
  setUser: (data: UserState['user']) => {
    userState.user = data;
    userState.isAuthenticated = !!data;
  },

  setAuthenticated: ({
    isAuthenticated,
    isAuthenticating,
  }: RequireAtLeastOne<
    Pick<UserState, 'isAuthenticated' | 'isAuthenticating'>
  >) => {
    if (
      isAuthenticated !== undefined &&
      isAuthenticated !== userState.isAuthenticated
    ) {
      userState.isAuthenticated = isAuthenticated;
    }

    if (
      isAuthenticating !== undefined &&
      isAuthenticating !== userState.isAuthenticating
    ) {
      userState.isAuthenticating = isAuthenticating;
    }
  },

  updateProcessingState: ({
    initializing,
    processing,
  }: RequireAtLeastOne<{ initializing: boolean; processing: boolean }>) => {
    if (processing !== undefined && processing !== userState.isProcessing) {
      userState.isProcessing = processing;
    }

    if (
      initializing !== undefined &&
      initializing !== userState.isInitializing
    ) {
      userState.isInitializing = initializing;
    }
  },

  clear: async () => {
    // valtio.pmnd.rs/docs/how-tos/how-to-reset-state
    Object.keys(initialUserState).forEach((key) => {
      // @ts-ignore
      userState[key as any] = initialUserState[key as keyof UserState];
    });
  },
};
